import type {
  Cart as V3Cart,
  CartProduct as V3CartProduct,
  CartCrmRedemption as V3CrmRedemption,
} from '@iheartjane/data-access';

import type {
  Cart as V1Cart,
  PendingCartProduct as V1CartProduct,
  PosCart as V1PosCart,
} from '@jane/shared/types';

// The v1 nested shapes are derived structurally from the v1 Cart/product so the
// mapper does not depend on named exports that may not be in the public barrel.
type V1BrandDiscount = V1Cart['brand_discounts'][number];
type V1CrmRedemption = NonNullable<V1Cart['crm_redemptions']>[number];
type V1UnavailableProduct = V1Cart['unavailable_products'][number];
type V1Inventory = NonNullable<V1CartProduct['inventories']>[number];
type V1Photo = NonNullable<V1CartProduct['photos']>[number];
// v3's pos-totals shape is not exported from data-access; derive it structurally.
type V3PosCartTotals = NonNullable<V3Cart['pos_cart']['totals']>;

/**
 * The mapper's input is the cart object the Redux slice holds (`state.cart.cart`),
 * which at runtime is the `@jane/shared/models` `Cart`, not the `@jane/shared/types`
 * `Cart` this module is otherwise built on. The two shapes have drifted: the models
 * cart widens several id fields to `string | number` and carries `amounts_manager`.
 * We model those runtime fields here — rather than importing `@jane/shared/models`
 * (client policy forbids it) or coupling this pure mapper to Redux — so the
 * coercions below are type-checked instead of silently trusted through a cast.
 */
export type V1CartInput = Omit<
  V1Cart,
  'id' | 'cart_discount_special_id' | 'group_special_id' | 'store'
> & {
  amounts_manager?: string;
  cart_discount_special_id?: string | number | null;
  fee_tax_amount?: number;
  group_special_id?: string | number | null;
  id?: string | number | null;
  status?: string;
  store?: { id: string | number } | null;
};

/**
 * Pure v1 → v3 cart mapper (ADR-0001). Bridges the legacy Redux cart shape into
 * the canonical v3 `Cart` shape so the Phase 3a facade can return v3 from day one
 * while still backed by v1 Redux. No React, no Redux, no I/O — a pure transform.
 * The whole `cartFacade/` directory (this mapper + the facade hooks) is deleted in
 * Phase 3d. TODO(carts-v3): remove once the v3 backing is the only source.
 */

/** v1 money strings → v3 `number | null` ('' and non-numeric → null). */
const toNumberOrNull = (value: string | null | undefined): number | null => {
  if (value == null) return null;
  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * v1 delivery-timestamp string → v3 `number | null`. These are epoch strings,
 * not decimal money strings, so only a pure numeric string is accepted; a
 * date-formatted value (e.g. '2026-07-01T00:00:00Z') maps to `null` rather than
 * the leading-digits parse `Number.parseFloat` would return (2026).
 */
const toEpochOrNull = (value: string | null | undefined): number | null => {
  if (value == null || !/^-?\d+(\.\d+)?$/.test(value)) return null;
  return Number(value);
};

/**
 * v1 `PosCartTotals` (all six fields optional) → the v3 pos-totals shape (all
 * six keys present as `number | null`); missing keys coalesce to `null`. Pure:
 * the facade reads the sibling `posCart` slice and passes the value in, so this
 * mapper stays Redux-free. `null`/absent totals map to `null` — matching the
 * flat-field fallback branch in `getCartLineItems`.
 */
const mapPosCartTotals = (
  totals: V1PosCart['totals'] | undefined
): V3PosCartTotals | null =>
  totals == null
    ? null
    : {
        delivery_fee: totals.delivery_fee ?? null,
        rounding_amount: totals.rounding_amount ?? null,
        sales_tax: totals.sales_tax ?? null,
        service_fee: totals.service_fee ?? null,
        store_tax: totals.store_tax ?? null,
        total: totals.total ?? null,
      };

/** v1 `tip_amount` string → v3 required `number` (non-numeric → 0). */
const toNumber = (value: string | null | undefined): number => {
  const parsed = Number.parseFloat(value ?? '');
  return Number.isNaN(parsed) ? 0 : parsed;
};

/** v1 numeric potency → v3 `string | null` (v3 carries these as strings). */
const toStringOrNull = (value: number | null | undefined): string | null =>
  value == null ? null : String(value);

/** v1 id-like fields are `string | number` at runtime (models drift) → v3 `number | null`. */
const toNumberIdOrNull = (
  value: string | number | null | undefined
): number | null => (value == null ? null : Number(value));

const mapInventory = (
  inventory: V1Inventory
): V3CartProduct['inventories'][number] => ({
  bulk: inventory.bulk,
  equivalency: inventory.equivalency,
  id: inventory.id,
  menu_product_id: inventory.menu_product_id,
  price_id: inventory.price_id,
  // v1 tax key is `apply_to_non_cannabis_item`; v3 pluralizes it.
  tax: inventory.tax
    ? {
        apply_to_discounted_price: inventory.tax.apply_to_discounted_price,
        apply_to_non_cannabis_items: inventory.tax.apply_to_non_cannabis_item,
        is_taxable: inventory.tax.is_taxable,
        rate: inventory.tax.rate,
        title: inventory.tax.title,
      }
    : null,
});

const mapPhoto = (photo: V1Photo): V3CartProduct['photos'][number] => ({
  // v1 photo id is a number; v3 carries it as a string.
  id: String(photo.id),
  position: photo.position,
  urls: photo.urls,
});

const mapCrmRedemption = (redemption: V1CrmRedemption): V3CrmRedemption => ({
  bypass_redemption: false,
  cart_uuid: redemption.cart_uuid,
  crm_provider: redemption.crm_provider,
  crm_redemption_id: redemption.crm_redemption_id ?? null,
  crm_reward_id: String(redemption.crm_reward_id),
  id: redemption.id ?? 0,
  point_value: redemption.point_value,
  redeemed: redemption.redeemed ?? false,
  redemption_failed: redemption.redemption_failed ?? null,
  reward: {
    amount: redemption.reward.amount ?? 0,
    type: redemption.reward.type ?? '',
  },
  reward_name: redemption.reward_name,
});

/**
 * Maps a v1 `PendingCartProduct` (a `MenuProduct`-derived shape) to a v3
 * `CartProduct`. Every v3 field is populated; fields with no v1 source get the
 * v3 type's zero value. `effects`/`flavors` are dropped to `[]` because the v1
 * `{id,title,value}` shape is structurally incompatible with v3's
 * `{id,kind,menu_product_id}` (there is no faithful conversion).
 */
export const mapV1CartProductToV3 = (
  product: V1CartProduct
): V3CartProduct => ({
  aggregate_rating: product.aggregate_rating ?? null,
  allow_multiple_flower_count: product.allow_multiple_flower_count,
  amount: product.amount ?? null,
  applicable_brand_special_ids: product.applicable_brand_special_ids ?? [],
  brand: product.brand ?? null,
  brand_discount_label: product.brand_discount_label ?? null,
  brand_discount_title: product.brand_discount_title ?? null,
  brand_discounted_price: null,
  brand_special_has_segment_rules:
    product.brand_special_has_segment_rules ?? false,
  brand_special_id: product.brand_special_id ?? null,
  brand_subtype: product.brand_subtype,
  category: product.category,
  cbd_dosage_milligrams: product.cbd_dosage_milligrams ?? 0,
  count: product.count,
  description: product.description,
  discounted_price_each: toNumberOrNull(product.discounted_price_each),
  discounted_price_eighth_ounce: toNumberOrNull(
    product.discounted_price_eighth_ounce
  ),
  discounted_price_gram: toNumberOrNull(product.discounted_price_gram),
  discounted_price_half_gram: toNumberOrNull(
    product.discounted_price_half_gram
  ),
  discounted_price_half_ounce: toNumberOrNull(
    product.discounted_price_half_ounce
  ),
  discounted_price_ounce: toNumberOrNull(product.discounted_price_ounce),
  discounted_price_quarter_ounce: toNumberOrNull(
    product.discounted_price_quarter_ounce
  ),
  discounted_price_two_gram: toNumberOrNull(product.discounted_price_two_gram),
  effects: [],
  flavors: [],
  id: product.id,
  image_urls: product.image_urls ?? [],
  inventories: (product.inventories ?? []).map(mapInventory),
  kind: product.kind,
  kind_subtype: null,
  max_cart_quantity: product.max_cart_quantity,
  // v1 has no per-weight-tier max_cart_quantity fields.
  max_cart_quantity_each: null,
  max_cart_quantity_eighth_ounce: null,
  max_cart_quantity_gram: null,
  max_cart_quantity_half_gram: null,
  max_cart_quantity_half_ounce: null,
  max_cart_quantity_ounce: null,
  max_cart_quantity_quarter_ounce: null,
  max_cart_quantity_two_gram: null,
  name: product.name,
  net_weight_grams: product.net_weight_grams,
  operator_store_rank: product.operator_store_rank ?? null,
  percent_cbd: toStringOrNull(product.percent_cbd),
  percent_thc: toStringOrNull(product.percent_thc),
  photos: (product.photos ?? []).map(mapPhoto),
  price_each: toNumberOrNull(product.price_each),
  price_eighth_ounce: toNumberOrNull(product.price_eighth_ounce),
  price_gram: toNumberOrNull(product.price_gram),
  price_half_gram: toNumberOrNull(product.price_half_gram),
  price_half_ounce: toNumberOrNull(product.price_half_ounce),
  price_id: product.price_id,
  price_ounce: toNumberOrNull(product.price_ounce),
  price_quarter_ounce: toNumberOrNull(product.price_quarter_ounce),
  price_two_gram: toNumberOrNull(product.price_two_gram),
  product_brand_id: product.product_brand_id ?? null,
  product_id: product.product_id,
  quantity_units: product.quantity_units ?? null,
  quantity_value: product.quantity_value ?? null,
  review_count: product.review_count ?? 0,
  root_subtype: product.root_subtype ?? null,
  special_amount: toNumberOrNull(product.special_amount),
  special_id: product.special_id ?? null,
  special_title: product.special_title ?? null,
  store_notes: product.store_notes ?? null,
  thc_dosage_milligrams: product.thc_dosage_milligrams ?? 0,
  type: product.type,
  url_slug: product.url_slug ?? null,
  visible: product.visible,
});

const mapBrandDiscount = (
  discount: V1BrandDiscount
): V3Cart['brand_discounts'][number] => ({
  brand_special_id: discount.brand_special_id,
  discount_total_amount: discount.discount_total_amount,
  discount_total_with_fee: discount.discount_total_with_fee,
  // v1 brand discounts carry no per-discount service-fee total.
  service_fee_total_amount: 0,
});

const mapUnavailableProduct = (
  product: V1UnavailableProduct
): V3Cart['unavailable_products'][number] => ({
  name: product.name,
  price_id: product.price_id,
  product_id: product.product_id,
});

/**
 * Maps the v1 cart object (`state.cart.cart`) to the canonical v3 `Cart` shape.
 * Deliberately dropped v1-only fields (`is_open`, `payment_method`, `tags`,
 * `message`) are re-homed by a later pass and get no v3 slot here.
 *
 * POS totals do not live on the v1 cart — they are a separate `posCart` slice.
 * The facade reads that slice and passes `posCartTotals` in; when omitted (or
 * `null`), `pos_cart.totals` is `null`, matching the un-fetched state.
 */
export const mapV1CartToV3 = (
  cart: V1CartInput,
  posCartTotals?: V1PosCart['totals']
): V3Cart => {
  const products = cart.products.map(mapV1CartProductToV3);
  return {
    all_discounts_total: cart.all_discounts_total ?? 0,
    amount: null,
    amounts_manager: cart.amounts_manager ?? '',
    // Prefer the flat name; fall back to the app_mode object's name.
    app_mode_name: cart.app_mode_name ?? cart.app_mode?.name ?? '',
    applied_specials: [],
    brand_discounts: cart.brand_discounts.map(mapBrandDiscount),
    brand_discounts_service_fee_total: cart.brand_discounts_service_fee_total,
    brand_discounts_total: cart.brand_discounts_total ?? 0,
    brand_discounts_total_with_fee: cart.brand_discounts_total_with_fee,
    brand_special_has_changed: cart.brand_special_has_changed,
    cart_discount_amount: cart.cart_discount_amount ?? 0,
    cart_discount_special_id: toNumberIdOrNull(cart.cart_discount_special_id),
    checked_out_at_with_store_timezone: null,
    created_at: null,
    crm_redemptions: (cart.crm_redemptions ?? []).map(mapCrmRedemption),
    delivery_fee_amount: cart.delivery_fee_amount ?? 0,
    delivery_finished_at: toEpochOrNull(cart.delivery_finished_at),
    delivery_started_at: toEpochOrNull(cart.delivery_started_at),
    discounted_product_total: cart.discounted_product_total ?? 0,
    discounted_subtotal: cart.discounted_subtotal ?? 0,
    dismissed_message: null,
    employee: null,
    fee_tax_amount: cart.fee_tax_amount ?? 0,
    group_discounts_total: cart.group_discounts_total ?? 0,
    group_special_id: toNumberIdOrNull(cart.group_special_id),
    group_special_title: cart.group_special_title ?? null,
    id: toNumberIdOrNull(cart.id) ?? 0,
    item_total: cart.item_total ?? 0,
    jane_pay_service_fee_amount: cart.jane_pay_service_fee_amount,
    loyalty_points_stacking_enabled: cart.loyalty_points_stacking_enabled,
    max_specials_per_cart: cart.max_specials_per_cart,
    max_specials_per_cart_hit: cart.max_specials_per_cart_hit,
    order_total: cart.order_total ?? 0,
    pos_cart: { totals: mapPosCartTotals(posCartTotals) },
    product_counts_have_been_capped: false,
    product_discounts_total: cart.product_discounts_total ?? 0,
    products,
    products_count: products.length,
    promo_code: cart.promo_code ?? null,
    promotions_opt_out: false,
    quantity_adjusted_products: cart.quantity_adjusted_products ?? [],
    rating: null,
    removed_crm_redemptions: (cart.removed_crm_redemptions ?? []).map(
      mapCrmRedemption
    ),
    reservation_mode: cart.reservation_mode,
    rounding_amount: cart.rounding_amount,
    sales_tax_amount: cart.sales_tax_amount,
    sales_tax_rate: cart.sales_tax_rate,
    service_fee_amount: cart.service_fee_amount ?? 0,
    special_has_changed: cart.special_has_changed,
    status: cart.status ?? '',
    store: cart.store ? { id: Number(cart.store.id) } : { id: 0 },
    store_tax_amount: cart.store_tax_amount ?? 0,
    suggested_specials: [],
    tax_included: cart.tax_included,
    tip_amount: toNumber(cart.tip_amount),
    unavailable_products: cart.unavailable_products.map(mapUnavailableProduct),
    uuid: cart.uuid,
  };
};
